import React, { useState, useEffect } from 'react';

interface DeploymentEnvironment {
  name: string;
  url: string;
  description: string;
}

const DEFAULT_ENVIRONMENTS: DeploymentEnvironment[] = [
  {
    name: 'Sandbox',
    url: 'https://colleagueassistant.connectwisedev.com',
    description: 'Main sandbox environment'
  },
  {
    name: 'Staging',
    url: 'https://staging.d1k4vm03cx3beu.amplifyapp.com',
    description: 'Legacy AWS Amplify staging (optional — prefer S3/CloudFront per Terraform)'
  },
  {
    name: 'Local Dev',
    url: 'http://localhost:5173',
    description: 'Local development server (Vite)'
  }
];

interface TeamsConfigPanelProps {
  currentUrl?: string;
  onUrlChange: (url: string) => void;
}

const TeamsConfigPanel: React.FC<TeamsConfigPanelProps> = ({ currentUrl, onUrlChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedEnvironment, setSelectedEnvironment] = useState<string>('');
  const [customUrl, setCustomUrl] = useState('');
  const [environments, setEnvironments] = useState<DeploymentEnvironment[]>(DEFAULT_ENVIRONMENTS);

  useEffect(() => {
    // Load saved environments from localStorage
    const savedEnvs = localStorage.getItem('teams-environments');
    if (savedEnvs) {
      try {
        const parsed = JSON.parse(savedEnvs);
        setEnvironments(parsed);
      } catch (e) {
        console.error('Failed to parse saved environments:', e);
      }
    }

    // Set current environment based on current URL
    const current = environments.find(env => env.url === currentUrl);
    if (current) {
      setSelectedEnvironment(current.name);
    } else {
      setCustomUrl(currentUrl || '');
    }
  }, [currentUrl, environments]);

  const saveEnvironments = (newEnvs: DeploymentEnvironment[]) => {
    setEnvironments(newEnvs);
    localStorage.setItem('teams-environments', JSON.stringify(newEnvs));
  };

  const handleEnvironmentSelect = (envName: string) => {
    const env = environments.find(e => e.name === envName);
    if (env) {
      setSelectedEnvironment(envName);
      setCustomUrl('');
      onUrlChange(env.url);
    }
  };

  const handleCustomUrlSubmit = () => {
    if (customUrl.trim()) {
      setSelectedEnvironment('');
      onUrlChange(customUrl.trim());
    }
  };

  const addCustomEnvironment = () => {
    if (customUrl.trim()) {
      const newEnv: DeploymentEnvironment = {
        name: `Custom ${environments.length + 1}`,
        url: customUrl.trim(),
        description: 'Custom environment'
      };
      const newEnvs = [...environments, newEnv];
      saveEnvironments(newEnvs);
      setSelectedEnvironment(newEnv.name);
      onUrlChange(newEnv.url);
    }
  };

  const removeEnvironment = (envName: string) => {
    const newEnvs = environments.filter(env => env.name !== envName);
    saveEnvironments(newEnvs);
    if (selectedEnvironment === envName) {
      setSelectedEnvironment('');
      setCustomUrl('');
    }
  };

  const getCurrentDisplayUrl = () => {
    const env = environments.find(e => e.name === selectedEnvironment);
    return env?.url || customUrl || currentUrl || 'Not set';
  };

  return (
    <>
      {/* Floating Config Button */}
      <div className="fixed bottom-4 right-4 z-50">
        <button
          onClick={() => setIsOpen(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white p-3 rounded-full shadow-lg transition-all duration-200 flex items-center justify-center"
          title="Configure Teams App URL"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        </button>
      </div>

      {/* Configuration Modal */}
      {isOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-black bg-opacity-50" onClick={() => setIsOpen(false)}></div>

            <div className="inline-block w-full max-w-lg p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Teams App Configuration</h3>
                <button
                  onClick={() => setIsOpen(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-4">
                {/* Current URL Display */}
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-sm text-gray-600 mb-1">Current URL:</p>
                  <p className="text-sm font-mono text-gray-800 break-all">{getCurrentDisplayUrl()}</p>
                </div>

                {/* Predefined Environments */}
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Quick Select:</h4>
                  <div className="space-y-2">
                    {environments.map((env) => (
                      <div key={env.name} className="flex items-center justify-between p-2 border rounded-lg hover:bg-gray-50">
                        <div className="flex-1">
                          <button
                            onClick={() => handleEnvironmentSelect(env.name)}
                            className={`text-left w-full ${selectedEnvironment === env.name ? 'text-blue-600 font-medium' : 'text-gray-700'}`}
                          >
                            <div className="font-medium">{env.name}</div>
                            <div className="text-xs text-gray-500">{env.description}</div>
                            <div className="text-xs font-mono text-gray-400 truncate">{env.url}</div>
                          </button>
                        </div>
                        {!DEFAULT_ENVIRONMENTS.find(defaultEnv => defaultEnv.name === env.name) && (
                          <button
                            onClick={() => removeEnvironment(env.name)}
                            className="ml-2 text-red-400 hover:text-red-600"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Custom URL Input */}
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Custom URL:</h4>
                  <div className="space-y-2">
                    <input
                      type="url"
                      value={customUrl}
                      onChange={(e) => setCustomUrl(e.target.value)}
                      placeholder="https://your-custom-deployment.com"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <div className="flex space-x-2">
                      <button
                        onClick={handleCustomUrlSubmit}
                        disabled={!customUrl.trim()}
                        className="flex-1 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                      >
                        Use URL
                      </button>
                      <button
                        onClick={addCustomEnvironment}
                        disabled={!customUrl.trim()}
                        className="flex-1 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                      >
                        Save as Environment
                      </button>
                    </div>
                  </div>
                </div>

                {/* Instructions */}
                <div className="bg-blue-50 p-3 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>Note:</strong> Changing the URL will update the Teams app configuration and may require reloading the Teams app.
                  </p>
                </div>
              </div>

              <div className="flex justify-end mt-6">
                <button
                  onClick={() => setIsOpen(false)}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TeamsConfigPanel;
